import React from 'react';
import { Code2, Database, Cloud, Wrench, Sparkles } from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { useLanguage } from '../language/LanguageContext';
import Tag from './ui/Tag';
import Reveal from './ui/Reveal';

interface SkillGroup {
  label: string;
  icon: LucideIcon;
  items: string[];
}

const SkillsSection = () => {
  const { translations } = useLanguage();
  const { skills } = translations;

  // Grouped by domain so 28 pills read as four ideas instead of one wall.
  const hardSkillGroups: SkillGroup[] = [
    {
      label: 'Languages & Frameworks',
      icon: Code2,
      items: ['JavaScript', 'Vue.js', 'React.js', 'Node.js', 'Nest.js', 'Ruby on Rails', 'React Native']
    },
    {
      label: 'Data',
      icon: Database,
      items: ['PostgreSQL', 'MySQL', 'SQL Server', 'MongoDB']
    },
    {
      label: 'Cloud',
      icon: Cloud,
      items: ['AWS Services', 'Azure DevOps']
    }
  ];

  const softSkills = ['Adaptability', 'Creativity', 'Empathy', 'Teamwork', 'Leadership'];

  const projectManagementTools = [
    'Git', 'JIRA', 'Agile', 'Scrum', 'Git Lab',
    'GitHub', 'Docker', 'CI/CD', 'Kubernetes', 'Azure DevOps'
  ];

  return (
    <section>
      <SectionHeader title={skills.title} />

      <Reveal>
        <h3 className="text-lg font-semibold text-dark-heading mb-4">{skills.hardSkills}</h3>
      </Reveal>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
        {hardSkillGroups.map((group, index) => (
          <Reveal
            key={group.label}
            delay={index * 70}
            className={index === 0 ? 'sm:col-span-2' : ''}
          >
            <div className="card-interactive h-full p-5">
              <div className="flex items-center gap-2.5 mb-4">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent/10 border border-accent/25 text-accent flex-shrink-0">
                  <group.icon size={16} />
                </span>
                <span className="text-sm font-medium text-dark-heading">{group.label}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {group.items.map((item) => (
                  <Tag key={item} onSurface>
                    {item}
                  </Tag>
                ))}
              </div>
            </div>
          </Reveal>
        ))}

        {/* Soft skills get a distinct treatment so the columns aren't redundant */}
        <Reveal delay={210} className="sm:col-span-2">
          <div className="card-interactive h-full p-5">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent/10 border border-accent/25 text-accent flex-shrink-0">
                <Sparkles size={16} />
              </span>
              <span className="text-sm font-medium text-dark-heading">{skills.softSkills}</span>
            </div>
            <ul className="flex flex-wrap gap-x-8 gap-y-2.5">
              {softSkills.map((skill) => (
                <li key={skill} className="flex items-center gap-3 text-dark-text">
                  <span
                    aria-hidden="true"
                    className="w-1.5 h-1.5 rounded-full bg-accent-gradient flex-shrink-0"
                  />
                  <span>{skill}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <Reveal delay={280}>
        <div className="flex items-center gap-2.5 mb-4">
          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent/10 border border-accent/25 text-accent flex-shrink-0">
            <Wrench size={16} />
          </span>
          <h3 className="text-lg font-semibold text-dark-heading">{skills.projectTools}</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {projectManagementTools.map((tool) => (
            <Tag key={tool}>{tool}</Tag>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

export default SkillsSection;
